import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Fab,
  Tooltip,
  Snackbar,
  Alert,
  Paper,
  InputAdornment,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { usePermissions } from '../../../context/PermissionContext';
import { API_BASE_URL } from '../../../constants/constants';
import Loader from '../../General/Loader';
import BackButton from '../../../components/BackButton';

const AdminPost = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { can } = usePermissions();

  // Edit / Create Dialog State
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postName, setPostName] = useState('');

  // Confirmation Modal State for Block / Deactivate
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [targetPostForBlock, setTargetPostForBlock] = useState(null);

  // Delete Confirmation State
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/posts`);
      setPosts(
        (response.data || []).map((post, index) => ({
          ...post,
          id: post._id || index,
          status: post.status || 'active',
        }))
      );
    } catch (error) {
      showSnackbar('Error fetching designations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleOpenDialog = (post = null) => {
    if (!can(post ? 'master.designations.edit' : 'master.designations.create')) return;
    if (post) {
      setEditMode(true);
      setSelectedPost(post);
      setPostName(post.postName);
    } else {
      setEditMode(false);
      setSelectedPost(null);
      setPostName('');
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setPostName('');
    setSelectedPost(null);
    setEditMode(false);
  };

  const handleSubmit = async () => {
    if (!postName.trim()) {
      showSnackbar('Please enter a designation name', 'error');
      return;
    }
    if (!can(editMode && selectedPost ? 'master.designations.edit' : 'master.designations.create')) return;

    try {
      if (editMode && selectedPost) {
        await axios.put(`${API_BASE_URL}/posts/${selectedPost.id}`, {
          postName: postName.trim(),
        });
        showSnackbar('Designation updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE_URL}/posts`, {
          postName: postName.trim(),
          moduleAssignments: [],
        });
        showSnackbar('Designation created successfully', 'success');
      }

      handleCloseDialog();
      fetchPosts();
    } catch (error) {
      const msg =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error submitting designation';
      showSnackbar(msg, 'error');
    }
  };

  const handleOpenConfirmBlock = (post) => {
    setTargetPostForBlock(post);
    setConfirmModalOpen(true);
  };

  const handleConfirmStatusToggle = async () => {
    if (!targetPostForBlock) return;
    if (!can('master.designations.block')) {
      showSnackbar('You do not have permission to block/unblock designations', 'error');
      setConfirmModalOpen(false);
      return;
    }

    const currentStatus = targetPostForBlock.status || 'active';
    const newStatus = currentStatus === 'active' ? 'blocked' : 'active';

    try {
      await axios.put(`${API_BASE_URL}/posts/${targetPostForBlock.id}`, {
        status: newStatus,
      });
      showSnackbar(
        `Designation "${targetPostForBlock.postName}" ${newStatus === 'blocked' ? 'blocked' : 'unblocked'} successfully`,
        'success'
      );
      fetchPosts();
    } catch (error) {
      showSnackbar('Error updating designation status', 'error');
    } finally {
      setConfirmModalOpen(false);
      setTargetPostForBlock(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (!can('master.designations.delete')) {
      showSnackbar('You do not have permission to delete designations', 'error');
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_BASE_URL}/posts/${deleteTarget.id}`);
      const msg = (response.data && response.data.message) || `Designation "${deleteTarget.postName}" deleted successfully`;
      showSnackbar(msg, 'success');
      fetchPosts();
    } catch (error) {
      const msg =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error deleting designation';
      showSnackbar(msg, 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleExportCSV = () => {
    if (!can('master.designations.export')) return;
    if (!filteredPosts || filteredPosts.length === 0) {
      showSnackbar('No designation data to export', 'warning');
      return;
    }

    const headers = ['SL', 'Designation ID', 'Designation Name', 'Status', 'Assigned Users'];
    const csvRows = [headers.join(',')];

    filteredPosts.forEach((post, idx) => {
      const sl = idx + 1;
      const id = post._id || post.id || 'N/A';
      const name = post.postName || 'N/A';
      const status = post.status || 'active';

      const assigned = typeof post.assignedUsers === 'number' ? post.assignedUsers : '';

      csvRows.push([sl, `"${id}"`, `"${name}"`, `"${status}"`, assigned].join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `manage_designations_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const filteredPosts = posts.filter((post) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (post.postName || '').toLowerCase();
    const id = String(post._id || post.id || '').toLowerCase();
    return name.includes(term) || id.includes(term);
  });

  const columns = [
    {
      field: 'sl',
      headerName: 'SL',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const index = filteredPosts.findIndex(
          (p) => (p.id || p._id) === (params.row.id || params.row._id)
        );
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
            {index >= 0 ? index + 1 : ''}
          </Typography>
        );
      },
    },
    {
      field: 'postName',
      headerName: 'Designation Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const status = params.value || 'active';
        const isBlocked = status === 'blocked' || status === 'inactive';
        return (
          <Chip
            label={isBlocked ? 'Blocked' : 'Active'}
            color={isBlocked ? 'error' : 'success'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        );
      },
    },
    {
      field: 'assignedUsers',
      headerName: 'Assigned Users',
      width: 140,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {typeof params.value === 'number' ? params.value : '-'}
        </Typography>
      ),
    },
  ];

  if (can('master.designations.block')) {
    columns.push({
        field: 'blockAction',
        headerName: 'Block / Unblock',
        width: 160,
        sortable: false,
        renderCell: (params) => {
          const isBlocked = params.row.status === 'blocked' || params.row.status === 'inactive';
          return (
            <Button
              variant="contained"
              size="small"
              color={isBlocked ? 'success' : 'error'}
              startIcon={<BlockIcon fontSize="small" />}
              onClick={() => handleOpenConfirmBlock(params.row)}
              sx={{ fontWeight: 700 }}
            >
              {isBlocked ? 'Unblock' : 'Block'}
            </Button>
          );
        },
      });
  }
  if (can('master.designations.edit')) {
    columns.push({
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => handleOpenDialog(params.row)}
            sx={{ fontWeight: 600 }}
          >
            Edit
          </Button>
        ),
      });
  }

  if (can('master.designations.delete')) {
    columns.push({
      field: 'deleteAction',
      headerName: 'Delete',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const assigned = params.row.assignedUsers;
        if (assigned === 0) {
          return (
            <Tooltip title="Delete designation">
              <IconButton color="error" size="small" onClick={() => setDeleteTarget(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }
        const title = typeof assigned === 'number'
          ? `Assigned to ${assigned} user(s)`
          : 'Assigned user count unavailable';
        return (
          <Tooltip title={title}>
            <span>
              <IconButton size="small" disabled>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    });
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ width: '100%', position: 'relative', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Manage Designation
          </Typography>

          {can('master.designations.export') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 700, mr: 7 }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {/* Filter / Search Bar */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search designation by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Loader />
        ) : (
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={filteredPosts}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
              rowHeight={60}
            />
          </Box>
        )}

        {can('master.designations.create') && (
          <Tooltip title="Create Designation">
            <Fab
              color="primary"
              aria-label="add"
              sx={{ position: 'absolute', top: -7, right: 16 }}
              onClick={() => handleOpenDialog()}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}

        {/* Edit / Create Designation Dialog */}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editMode ? 'Edit Designation' : 'Create New Designation'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Designation Name"
              variant="outlined"
              fullWidth
              value={postName}
              onChange={(e) => setPostName(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {editMode ? 'Update' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Modal for Block / Unblock */}
        <Dialog
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
            Confirm Status Change
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ color: '#334155', fontWeight: 500 }}>
              {targetPostForBlock && (
                <>
                  Are you sure you want to{' '}
                  <strong>
                    {(targetPostForBlock.status || 'active') === 'active' ? 'block' : 'unblock'}
                  </strong>{' '}
                  designation <strong>{targetPostForBlock.postName}</strong>?
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmModalOpen(false)} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmStatusToggle}
              color={
                targetPostForBlock && (targetPostForBlock.status || 'active') === 'active'
                  ? 'error'
                  : 'success'
              }
              variant="contained"
              sx={{ fontWeight: 700 }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Modal for Delete */}
        <Dialog
          open={Boolean(deleteTarget)}
          onClose={() => !deleting && setDeleteTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
            Delete Designation
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ color: '#334155', fontWeight: 500 }}>
              {deleteTarget && (
                <>
                  Are you sure you want to permanently delete designation{' '}
                  <strong>{deleteTarget.postName}</strong>? This cannot be undone.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteTarget(null)} color="inherit" variant="outlined" disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              sx={{ fontWeight: 700 }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default AdminPost;